import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  getCurrentUser, 
  signIn, 
  signOut, 
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import awsConfig from '../config/aws-config';

// Configure Amplify
Amplify.configure(awsConfig);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [challengeName, setChallengeName] = useState(null);
  const [challengeUser, setChallengeUser] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        attributes: session.tokens?.idToken?.payload || {},
      });
      setChallengeName(null);
      setChallengeUser(null);
    } catch (err) {
      console.log('Not authenticated:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setError(null);
      const result = await signIn({ username, password });
      
      // Check if there's a challenge (like NEW_PASSWORD_REQUIRED)
      if (result.isSignedIn === false && result.nextStep) {
        const step = result.nextStep.signInStep;
        
        if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setChallengeName('NEW_PASSWORD_REQUIRED');
          setChallengeUser({ username, signInStep: result.nextStep });
          return result;
        }
      }
      
      // If fully signed in, update user
      if (result.isSignedIn) {
        await checkUser();
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const completeNewPassword = async (newPassword) => {
    try {
      setError(null);
      
      if (!challengeUser) {
        throw new Error('No active password challenge');
      }

      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (result.isSignedIn) {
        await checkUser();
        setChallengeName(null);
        setChallengeUser(null);
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setChallengeName(null);
      setChallengeUser(null);
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err.message);
    }
  };

  const forgotPassword = async (username) => {
    try {
      setError(null);
      const result = await resetPassword({ username });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const confirmForgotPassword = async (username, confirmationCode, newPassword) => {
    try {
      setError(null);
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    challengeName,
    login,
    logout,
    checkUser,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;

